// @ts-nocheck
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Icon, { ICONS } from '../icons';
import { useLocalization, type Language } from '../../../config/localization';

interface Option {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  className?: string;
  id?: string;
  disabled?: boolean;
  language?: Language;
}

const MAX_RENDERED_OPTIONS = 50;
const MAX_MENU_HEIGHT = 240;
const MENU_GAP = 4;
const VIEWPORT_PADDING = 8;

export default function MultiSelect({
  options,
  value = [],
  onChange,
  placeholder = 'Select options...',
  className = '',
  id,
  disabled = false,
  language = 'en'
}: MultiSelectProps) {
  const t = useLocalization(language);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [position, setPosition] = useState<{
    x: number;
    top?: number;
    bottom?: number;
    width: number;
    maxHeight: number;
  }>({
    x: 0,
    width: 0,
    maxHeight: MAX_MENU_HEIGHT
  });
  const [positionReady, setPositionReady] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const closeDropdown = () => {
    setIsOpen(false);
    setSearchTerm('');
    setPositionReady(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!selectRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        closeDropdown();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeDropdown();
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      const rect = selectRef.current?.getBoundingClientRect();
      if (!rect) return;

      const availableBelow = Math.max(
        0,
        window.innerHeight - rect.bottom - MENU_GAP - VIEWPORT_PADDING
      );
      const availableAbove = Math.max(0, rect.top - MENU_GAP - VIEWPORT_PADDING);
      const openAbove = availableBelow < MAX_MENU_HEIGHT && availableAbove > availableBelow;
      const maxHeight = Math.min(
        MAX_MENU_HEIGHT,
        openAbove ? availableAbove : availableBelow
      );
      setPosition({
        x: rect.left,
        top: openAbove ? undefined : rect.bottom + MENU_GAP,
        bottom: openAbove ? window.innerHeight - rect.top + MENU_GAP : undefined,
        width: rect.width,
        maxHeight
      });
      setPositionReady(true);
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  const handleToggle = (optionValue: string) => {
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  const handleRemove = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter(v => v !== optionValue));
  };

  const selectedOptions = options.filter(opt => value.includes(opt.value));
  const hasSearch = options.length > 8;
  const filteredOptions = hasSearch
    ? options.filter(option =>
        String(option.label ?? '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;
  const visibleOptions = filteredOptions.slice(0, MAX_RENDERED_OPTIONS);

  useEffect(() => {
    if (!hasSearch && searchTerm) setSearchTerm('');
  }, [hasSearch, searchTerm]);

  const dropdown = isOpen && !disabled && positionReady ? (
    <div
      ref={menuRef}
      data-multi-select-dropdown
      className="fixed bg-gray-800 border border-gray-600 rounded-lg shadow-lg max-h-60 overflow-auto"
      style={{
        left: position.x,
        top: position.top,
        bottom: position.bottom,
        width: position.width,
        maxHeight: position.maxHeight,
        zIndex: 999999
      }}
    >
      {hasSearch && (
        <div className="sticky top-0 bg-gray-800 p-2">
          <input
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={t.widgets?.searchOptions || 'Search options...'}
            aria-label={t.widgets?.searchOptionsLabel || 'Search options'}
            className="w-full rounded border border-gray-600 bg-gray-700 px-3 py-2 text-sm text-gray-200 placeholder:text-gray-400 focus:border-purple-500 focus:outline-none"
          />
        </div>
      )}
      {visibleOptions.map(option => {
        const isSelected = value.includes(option.value);
        return (
          <div
            key={option.value}
            onClick={() => handleToggle(option.value)}
            className={`
              px-3 py-2 cursor-pointer transition-colors duration-150
              ${isSelected
                ? 'bg-purple-600/20 text-purple-300'
                : 'text-gray-300 hover:bg-gray-700'
              }
            `}
          >
            <div className="flex items-center gap-2">
              <div
                className={`
                  w-4 h-4 border rounded flex items-center justify-center
                  ${isSelected
                    ? 'bg-purple-600 border-purple-600'
                    : 'border-gray-500'
                  }
                `}
              >
                {isSelected && (
                  <Icon name={ICONS.CHECK} size={10} className="text-white" />
                )}
              </div>
              {option.label}
            </div>
          </div>
        );
      })}
      {filteredOptions.length > MAX_RENDERED_OPTIONS && (
        <div className="px-3 py-2 text-xs text-gray-400" role="status">
          {(t.widgets?.refineOptions || 'Showing the first {count} options. Refine your search.')
            .replace('{count}', String(MAX_RENDERED_OPTIONS))}
        </div>
      )}
    </div>
  ) : null;

  return (
    <>
    <div ref={selectRef} className={`relative ${className}`}>
      <div
        id={id}
        onClick={() => {
          if (disabled) return;
          if (isOpen) {
            closeDropdown();
          } else {
            setPositionReady(false);
            setIsOpen(true);
          }
        }}
        className={`
          min-h-[38px] px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg
          cursor-pointer transition-colors duration-200
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-500'}
          ${isOpen ? 'border-purple-500 ring-1 ring-purple-500/20' : ''}
        `}
      >
        {selectedOptions.length === 0 ? (
          <span className="text-gray-400">{placeholder}</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {selectedOptions.map(option => (
              <span
                key={option.value}
                className="inline-flex items-center gap-1 px-2 py-1 bg-purple-600/20 border border-purple-500/30 rounded text-xs text-purple-300"
              >
                {option.label}
                <button
                  type="button"
                  onClick={(e) => handleRemove(option.value, e)}
                  className="hover:text-purple-100 transition-colors"
                  disabled={disabled}
                >
                  <Icon name={ICONS.X} size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
        
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <Icon
            name={ICONS.CHEVRON_LEFT}
            size={16}
            className={`text-gray-400 transition-transform duration-200 ${
              isOpen ? '-rotate-90' : 'rotate-90'
            }`}
          />
        </div>
      </div>

    </div>
    {dropdown && createPortal(dropdown, document.body)}
    </>
  );
}
